import { useLayoutEffect, useState } from "react";
import ResizeObserver from "resize-observer-polyfill";
import { getScrollParent } from "../../../../lib/scroll.js";
import { getPassiveArg } from "../../../../lib/feature-detection.js";

const getZIndex = (node) => {
  if (!node) return;
  let currentNode = node;
  let lastZIndex = 0;
  while (currentNode.offsetParent) {
    currentNode = currentNode.offsetParent;
    const zIndexAsString = window
      .getComputedStyle(currentNode)
      .getPropertyValue("z-index");
    if (zIndexAsString !== "auto") {
      lastZIndex = parseInt(
        window.getComputedStyle(currentNode).getPropertyValue("z-index"),
        10,
      );
    }
  }
  return lastZIndex;
};

export const useZIndex = (node) => {
  const [zIndex, setZIndex] = useState(node ? 5 : null);
  useLayoutEffect(() => {
    if (node) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setZIndex(getZIndex(node));
    } else {
      setZIndex(null);
    }
  }, [node, setZIndex]);
  return zIndex;
};

const adaptRectToWindowScroll = (d) => ({
  ...d,
  top: d.top + window.pageYOffset,
  bottom: d.bottom + window.pageYOffset,
  left: d.left + window.pageXOffset,
  right: d.right + window.pageXOffset,
});

export const usePosition = (node) => {
  const [bounds, setBounds] = useState(null);

  useLayoutEffect(() => {
    if (!node) return;

    const scrollPane = getScrollParent(node);

    const updatePosition = () => {
      const rect = node.getBoundingClientRect();
      const { top, bottom, left, right } =
        scrollPane === window ? adaptRectToWindowScroll(rect) : rect;
      setBounds((prev) => {
        if (
          !prev ||
          prev.top !== top ||
          prev.bottom !== bottom ||
          prev.left !== left ||
          prev.right !== right
        ) {
          return {
            top,
            bottom,
            left,
            right,
            height: bottom - top,
            width: right - left,
          };
        } else {
          return prev;
        }
      });
    };

    const ro = new ResizeObserver(updatePosition);
    ro.observe(node);
    const intervalId = setInterval(updatePosition, 1000);
    const scrollHandler = () => {
      window.requestAnimationFrame(updatePosition);
    };
    scrollPane.addEventListener("scroll", scrollHandler, getPassiveArg());
    window.addEventListener("resize", scrollHandler);
    return () => {
      clearInterval(intervalId);
      scrollPane.removeEventListener("scroll", scrollHandler);
      window.removeEventListener("resize", scrollHandler);
      ro.disconnect();
    };
  }, [node]);

  return bounds;
};
